/**
 * @name umi 的路由配置
 * @description 只支持 path,component,routes,redirect,wrappers,name,icon 的配置
 * @param path  path 只支持两种占位符配置，第一种是动态参数 :id 的形式，第二种是 * 通配符，通配符只能出现路由字符串的最后。
 * @param component 配置 location 和 path 匹配后用于渲染的 React 组件路径。可以是绝对路径，也可以是相对路径，如果是相对路径，会从 src/pages 开始找起。
 * @param routes 配置子路由，通常在需要为多个路径增加 layout 组件时使用。
 * @param redirect 配置路由跳转
 * @param wrappers 配置路由组件的包装组件，通过包装组件可以为当前的路由组件组合进更多的功能。 比如，可以用于路由级别的权限校验
 * @param name 配置路由的标题，默认读取国际化文件 menu.ts 中 menu.xxxx 的值，如配置 name 为 login，则读取 menu.ts 中 menu.login 的取值作为标题
 * @param icon 配置路由的图标，取值参考 https://ant.design/components/icon-cn， 注意去除风格后缀和大小写，如想要配置图标为 <StepBackwardOutlined /> 则取值应为 stepBackward 或 StepBackward，如想要配置图标为 <UserOutlined /> 则取值应为 user 或者 User
 * @doc https://umijs.org/docs/guides/routes
 */
export default [
  {
    path: '/',
    component: './landing',
    layout: false,
    hideInMenu: true,
  },
  {
    path: '/docs',
    component: './docs',
    layout: false,
    hideInMenu: true,
  },
  {
    path: '/user',
    layout: false,
    routes: [
      {
        path: 'login',
        layout: false,
        name: 'login',
        component: './user/login',
      },
      {
        path: 'reset-password',
        layout: false,
        name: 'reset-password',
        component: './user/reset-password',
      },
      {
        path: '',
        redirect: '/user/login',
      },
      {
        component: '404',
        path: '*',
      },
    ],
  },
  {
    path: '/403',
    component: './exception/403',
    name: '403',
  },

  // ── 1. 控制台 ──────────────────────────────────────────────────────────
  {
    path: '/dashboard',
    name: 'dashboard',
    icon: 'home',
    routes: [
      {
        path: '',
        redirect: '/dashboard/workplace',
      },
      {
        name: 'workplace',
        path: 'workplace',
        component: './dashboard/workplace',
        permission: 'dashboard:query',
      },
    ],
  },

  // ── 2. 应用中心 ────────────────────────────────────────────────────────
  {
    path: '/app',
    name: 'app',
    icon: 'appstore',
    routes: [
      {
        path: '',
        redirect: '/app/mine',
      },
      {
        name: 'mine',
        path: 'mine',
        component: './app/mine',
        permission: 'app:query',
      },
      {
        name: 'mgmt',
        path: 'mgmt',
        routes: [
          {
            path: '',
            redirect: '/app/mgmt/list',
          },
          {
            name: 'list',
            path: 'list',
            component: './app/mgmt/list',
            permission: 'appAdmin:query',
          },
          {
            name: 'create',
            path: 'list/create',
            component: './app/mgmt/list/create',
            hideInMenu: true,
            permission: 'appAdmin:add',
            parentKeys: ['/app/mgmt/list'],
          },
          {
            name: 'edit',
            path: 'list/edit/:id',
            component: './app/mgmt/list/edit',
            hideInMenu: true,
            permission: 'appAdmin:edit',
            parentKeys: ['/app/mgmt/list'],
          },
          {
            name: 'detail',
            path: 'list/detail/:id',
            component: './app/mgmt/list/detail',
            hideInMenu: true,
            permission: 'appAdmin:query',
            parentKeys: ['/app/mgmt/list'],
          },
          {
            name: 'analysis',
            path: 'analysis',
            component: './app/mgmt/analysis',
            permission: 'appAnalysis:query',
          },
          {
            name: 'appName',
            path: ':appType/*',
            hideInMenu: true,
            wrappers: ['@/wrappers/PageContainerWrapper'],
            microApp: 'app-management',
          },
        ],
      },
      {
        name: 'appName',
        path: ':appType/*',
        hideInMenu: true,
        wrappers: ['@/wrappers/PageContainerWrapper'],
        microApp: 'app',
      },
    ],
  },

  // ── 3. 数据管理 ────────────────────────────────────────────────────────
  {
    path: '/data',
    name: 'data',
    icon: 'database',
    routes: [
      {
        path: '',
        redirect: '/data/dataset',
      },
      {
        name: 'dataset',
        path: 'dataset',
        component: './data/dataset',
        permission: 'knowledgeAdmin:query',
      },
      {
        name: 'dataset.create',
        path: 'dataset/create',
        component: './data/dataset/create',
        hideInMenu: true,
        permission: 'knowledgeAdmin:add',
        parentKeys: ['/data/dataset'],
      },
      {
        name: 'dataset.edit',
        path: 'dataset/edit/:id',
        component: './data/dataset/edit',
        hideInMenu: true,
        permission: 'knowledgeAdmin:edit',
        keepAliveName: 'knowledgeAdmin-edit',
        parentKeys: ['/data/dataset'],
      },
      {
        name: 'dataset.detail',
        path: 'dataset/:pageType/*',
        microApp: 'dataset',
        wrappers: ['@/wrappers/PageContainerWrapper'],
        hideInMenu: true,
        permission: 'dataset:file:query',
        parentKeys: ['/data/dataset'],
      },
      {
        name: 'dataset.detail.file',
        path: 'chunk/*',
        microApp: 'dataset',
        wrappers: ['@/wrappers/PageContainerWrapper'],
        hideInMenu: true,
        parentKeys: ['/data/dataset'],
      },
      {
        name: 'file',
        path: 'file',
        microApp: 'file',
        wrappers: ['@/wrappers/PageContainerWrapper'],
        permission: 'file:query',
      },
    ],
  },

  // ── 4. 统计分析 ────────────────────────────────────────────────────────
  {
    path: '/analysis',
    name: 'analysis',
    icon: 'barChart',
    routes: [
      {
        path: '',
        redirect: '/analysis/access',
      },
      {
        name: 'access',
        path: 'access',
        routes: [
          {
            path: '',
            redirect: '/analysis/access/login',
          },
          {
            name: 'login',
            path: 'login',
            component: './analysis/access/login',
            permission: 'userLogin:query',
          },
          {
            name: 'action',
            path: 'action',
            component: './analysis/access/action',
            permission: 'userClickEvent:query',
          },
          {
            name: 'app',
            path: 'app',
            component: './analysis/access/app',
            permission: 'appAccess:query',
          },
          {
            name: 'app-detail',
            path: 'app/detail/:id',
            component: './analysis/access/app/detail',
            hideInMenu: true,
            permission: 'appAccess:query',
            parentKeys: ['/analysis/access/app'],
          },
          // {
          //   name: 'user-login-detail',
          //   path: 'user-login-detail',
          //   component: './analysis/access/user-login-detail',
          //   hideInMenu: true,
          //   permission: 'userLogin:query',
          //   parentKeys: ['/analysis/access/login'],
          // }
        ],
      },
      {
        name: 'audit',
        path: 'audit',
        routes: [
          {
            path: '',
            redirect: '/analysis/audit/user',
          },
          {
            name: 'user',
            path: 'user',
            component: './analysis/audit/user',
            permission: 'userStatis:query',
          },
          {
            name: 'role-assign',
            path: 'role-assign',
            component: './analysis/audit/role-assign',
            permission: 'roleStatis:query',
          },
          {
            name: 'role-detail',
            path: 'role-detail',
            component: './analysis/audit/role-detail',
            permission: 'roleDetail:query',
          },
          {
            name: 'app',
            path: 'app',
            component: './analysis/audit/app',
            permission: 'appStatis:query',
          },
        ],
      },
    ],
  },

  // ── 5. 组织管理 ────────────────────────────────────────────────────────
  {
    path: '/org',
    name: 'org',
    icon: 'team',
    routes: [
      {
        path: '',
        redirect: '/org/user',
      },
      {
        name: 'user',
        path: 'user',
        component: './org/user',
        permission: 'user:query',
      },
      {
        name: 'user.permission',
        path: 'user/permission/:id',
        component: './org/user/permission',
        hideInMenu: true,
        permission: 'user:edit',
        keepAliveName: 'user-permission',
        parentKeys: ['/org/user'],
      },
      {
        name: 'user.add',
        path: 'user/add',
        component: './org/user/add',
        hideInMenu: true,
        permission: 'user:add',
        parentKeys: ['/org/user'],
      },
      {
        name: 'user.batchImportUser',
        path: 'user/batchImportUser',
        component: './org/user/batchImportUser',
        hideInMenu: true,
        permission: 'user:add',
        parentKeys: ['/org/user'],
      },
      {
        name: 'user.edit',
        path: 'user/edit/:id',
        component: './org/user/edit',
        hideInMenu: true,
        permission: 'user:edit',
        keepAliveName: 'user-edit',
        parentKeys: ['/org/user'],
      },
      {
        name: 'user.detail',
        path: 'user/detail/:id',
        component: './org/user/detail',
        hideInMenu: true,
        permission: 'user:query',
        keepAliveName: 'user-detail',
        parentKeys: ['/org/user'],
      },
      {
        name: 'role',
        path: 'role',
        component: './org/role',
        permission: 'role:query',
      },
      {
        name: 'role.add',
        path: 'role/add',
        component: './org/role/add',
        hideInMenu: true,
        permission: 'role:add',
        parentKeys: ['/org/role'],
      },
      {
        name: 'role.edit',
        path: 'role/edit/:id',
        component: './org/role/edit',
        hideInMenu: true,
        permission: 'role:edit',
        keepAliveName: 'role-edit',
        parentKeys: ['/org/role'],
      },
      {
        name: 'role.copy',
        path: 'role/copy/:id',
        component: './org/role/copy',
        hideInMenu: true,
        permission: 'role:add',
        keepAliveName: 'role-copy',
        parentKeys: ['/org/role'],
      },
      {
        name: 'company',
        path: 'company',
        component: './org/company',
        permission: 'company:query',
      },
      {
        name: 'company.create',
        path: 'company/create',
        component: './org/company/create',
        hideInMenu: true,
        permission: 'company:add',
        parentKeys: ['/org/company'],
      },
      {
        name: 'company.edit',
        path: 'company/edit/:id',
        component: './org/company/edit',
        hideInMenu: true,
        permission: 'company:edit',
        parentKeys: ['/org/company'],
      },
      {
        name: 'company.setting',
        path: 'company/setting/:id',
        component: './org/company/setting',
        hideInMenu: true,
        permission: 'company:edit',
        parentKeys: ['/org/company'],
      },
      {
        name: 'dept',
        path: 'dept',
        component: './org/dept',
        permission: 'department:query',
      },
      {
        name: 'dept.create',
        path: 'dept/create',
        component: './org/dept/create',
        hideInMenu: true,
        permission: 'department:add',
        parentKeys: ['/org/dept'],
      },
      {
        name: 'dept.edit',
        path: 'dept/edit/:id',
        component: './org/dept/edit',
        hideInMenu: true,
        permission: 'department:edit',
        parentKeys: ['/org/dept'],
      },
    ],
  },

  // ── 6. 系统设置 ────────────────────────────────────────────────────────
  {
    path: '/system',
    name: 'system',
    icon: 'setting',
    routes: [
      {
        path: '',
        redirect: '/system/menu',
      },
      {
        name: 'menu',
        path: 'menu',
        component: './system/menu',
        permission: 'menu:query',
      },
      {
        name: 'app-config',
        path: 'app-config',
        microApp: 'ragflow',
        keepAliveName: 'system-app-config',
        permission: 'appConfig:query',
        wrappers: ['@/wrappers/PageContainerWrapper'],
      },
      {
        name: 'data-source',
        path: 'data-source/*',
        microApp: 'ragflow',
        keepAliveName: 'system-data-source',
        permission: 'dataSource:query',
        wrappers: ['@/wrappers/PageContainerWrapper'],
      },
      {
        path: 'data-source/*',
        microApp: 'ragflow',
        hideInMenu: true,
        wrappers: ['@/wrappers/PageContainerWrapper'],
        keepAliveName: 'system-data-source',
        permission: 'dataSource:query',
        parentKeys: ['/system/data-source']
      },
      {
        name: 'config',
        path: 'config',
        component: './system/config',
        permission: 'config:query',
      },
      {
        name: 'license',
        path: 'license',
        component: './system/license',
        permission: 'tenantAuth:query',
      },
      {
        name: 'changelog',
        path: 'changelog',
        component: './system/changelog',
        permission: 'changelog:query',
      },
      {
        name: 'info',
        path: 'info',
        component: './system/info',
        permission: 'sysInfo:query',
      },
    ],
  },

  // ── microApp 全屏路由 ──────────────────────────────────────────────────
  {
    path: '/document/*',
    name: 'document',
    icon: 'file.preview',
    microApp: 'file-preview',
    layout: false,
    hideInMenu: true,
  },
  {
    path: '/data/datasets',
    name: 'data.dataset',
    icon: 'appstore',
    redirect: '/data/dataset',
  },
  {
    path: '/user-setting/model',
    component: './user-setting/model-redirect',
    hideInMenu: true,
    layout: false,
  },
  {
    path: '/user-setting/data-source',
    component: './user-setting/data-source-redirect',
    hideInMenu: true,
    layout: false,
  },
  {
    path: '/user-setting/data-source/*',
    component: './user-setting/data-source-redirect',
    hideInMenu: true,
    layout: false,
  },
  {
    path: '/data/user-setting/data-source',
    component: './user-setting/data-source-redirect',
    hideInMenu: true,
    layout: false,
  },
  {
    path: '/data/user-setting/data-source/*',
    component: './user-setting/data-source-redirect',
    hideInMenu: true,
    layout: false,
  },
  // ── 异常页 ────────────────────────────────────────────────────────────
  {
    name: 'exception',
    icon: 'warning',
    path: '/exception',
    wrappers: ['@/components/KeepAlive'],
    routes: [
      {
        path: '',
        redirect: '/exception/403',
      },
      {
        name: '403',
        icon: 'smile',
        path: '403',
        component: './exception/403',
      },
      {
        name: '404',
        icon: 'smile',
        path: '404',
        component: './exception/404',
      },
      {
        name: '500',
        icon: 'smile',
        path: '500',
        component: './exception/500',
      },
    ],
  },
  {
    component: '404',
    path: '/*',
  },
];
